import { type FunctionComponent } from 'react';
import type React from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import type { ProductCardProps } from '@/core/tamagoshi/ProductCard/ProductCard';
import type { SpartacuxProductDiscoveryDomainState } from '@/domains/productDiscovery/productDiscovery.model';
import { RecommendationPlaceholder } from '@/domains/productDiscovery/ProductPage/b2c/components/Recommendation/Recommendation.placeholder';
import type { Product } from '@/domains/productDiscovery/Recommendation/interfaces/product';
import type { SpartacuxShoppingCartState } from '@/domains/shoppingCart/ShoppingCartState';

import { Playlist } from '../../components/Playlists/components/Playlist/Playlist';
import { RecommendationPlaylistSkeleton } from '../../components/Playlists/components/RecommendationPlaylistSkeleton/RecommendationPlaylistSkeleton';
import type { ProcessedRecommendationItem } from '../../interfaces/msRecommendation';
import type { RecommendationPlaylist } from '../../interfaces/productRecommendation';
import { selectProductRecommendationsFetchingState } from '../../selectors/productRecommendation';

export interface ProductRecommendationProps {
  className?: string;
  sliderClassName?: string;
  controlsClassName?: string;
  arrowsColorClassName?: string;
  arrowsDisabledColorClassName?: string;
  placeHolderSelector: (
    state: Partial<
      SpartacuxProductDiscoveryDomainState & SpartacuxShoppingCartState
    >,
  ) => Partial<ProcessedRecommendationItem> | undefined;
  recommendationSelector: (
    state: SpartacuxProductDiscoveryDomainState & SpartacuxShoppingCartState,
  ) => RecommendationPlaylist | undefined;
  withProductCardsLight?: boolean;
  playlistLocation: string;
  displayDeliveryLabel?: boolean;
  addToCart?: ProductCardProps['addToCart'];
  addToCartButtonVariant?: ProductCardProps['addToCartButtonVariant'];
  position?: number;
  blockClicked?: string;
  displayAddToCartModal?: boolean;
  displayAddToCartCustomSummary?: boolean;
  autoUpdateAddToCartStateDisabled?: boolean;
  titleLabel?: string;
  CustomPriceComponent?: React.ComponentType<{
    product: Product;
  }>;
  legalMention?: boolean;
  isBrandsFilterEnabled?: boolean;
  isCategoriesFilterEnabled?: boolean;
  withSideArrows?: boolean;
  hasPlaceholder?: boolean;
}

export const ProductRecommendation: FunctionComponent<
  ProductRecommendationProps
> = ({
  className,
  sliderClassName,
  controlsClassName,
  arrowsColorClassName,
  arrowsDisabledColorClassName,
  withProductCardsLight,
  playlistLocation,
  placeHolderSelector,
  recommendationSelector,
  displayDeliveryLabel,
  addToCart,
  addToCartButtonVariant,
  position,
  blockClicked,
  displayAddToCartModal,
  displayAddToCartCustomSummary,
  autoUpdateAddToCartStateDisabled,
  titleLabel,
  legalMention,
  isBrandsFilterEnabled = false,
  isCategoriesFilterEnabled = false,
  withSideArrows = false,
  hasPlaceholder = true,
}) => {
  const config = useSelector(placeHolderSelector);

  const productRecommendation = useSelector(
    recommendationSelector,
    shallowEqual,
  );

  const isLoading = useSelector(selectProductRecommendationsFetchingState);

  if (productRecommendation) {
    return (
      <Playlist
        trackingInfos={{ titleLabel, blockClicked }}
        className={className}
        playlistLocation={playlistLocation}
        sliderClassName={sliderClassName}
        controlsClassName={controlsClassName}
        arrowsColorClassName={arrowsColorClassName}
        arrowsDisabledColorClassName={arrowsDisabledColorClassName}
        playlist={productRecommendation}
        withProductCardsLight={withProductCardsLight}
        displayDeliveryLabel={displayDeliveryLabel}
        addToCart={addToCart}
        addToCartButtonVariant={addToCartButtonVariant}
        position={position}
        displayAddToCartModal={displayAddToCartModal}
        displayAddToCartCustomSummary={displayAddToCartCustomSummary}
        autoUpdateAddToCartStateDisabled={autoUpdateAddToCartStateDisabled}
        titleLabel={titleLabel}
        legalMention={legalMention}
        isBrandsFilterEnabled={isBrandsFilterEnabled}
        isCategoriesFilterEnabled={isCategoriesFilterEnabled}
        withSideArrows={withSideArrows}
      />
    );
  }

  if (config && isLoading) {
    const bundle = config.displayType === 'bundle';
    const configItems =
      config.categoryIds || config.productIds || config.modelIds;
    const configSize = configItems?.length;

    return configSize && !bundle ? (
      <RecommendationPlaylistSkeleton
        withProductCardsLight={withProductCardsLight}
        key={config.originalName}
        addToCart={addToCart}
        hasFilters={isBrandsFilterEnabled || isCategoriesFilterEnabled}
      />
    ) : null;
  }

  if (config && !isLoading && hasPlaceholder) {
    return <RecommendationPlaceholder withAddToCart={addToCart} />;
  }

  return null;
};
